import { useEffect, useRef, useState } from 'react';
import useFetch from 'use-http';
import { API_BASE_URL, KVASIR_BASE_URL } from '../config';
import { Stack } from '@fluentui/react/lib/Stack';
import { TextField } from '@fluentui/react/lib/TextField';
import { storeValue } from '../utils/storage';
import {
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Spinner,
  SpinnerSize,
} from '@fluentui/react';
import messages from './messages';
import { useIntl } from 'react-intl';
import { captureCallbackParams } from '../app/auth/callbackParams';
import { ExchangeResult, exchangeCode, startPkce } from '../app/auth/pkce';

interface User {
  is_provisioned: boolean;
  redirect_url: string;
}

type SSOFailure = Exclude<ExchangeResult, { ok: true }>['reason'];

// POST /session/user, carrying the PKCE state and challenge so that kvasir
// returns a single-use code instead of the token (DEV-1032). If WebCrypto or
// sessionStorage is unavailable the request goes without them and kvasir falls
// back to its legacy behaviour.
async function checkSSO(email: string): Promise<User> {
  const url = new URL(KVASIR_BASE_URL() + '/session/user');
  url.searchParams.set('dest', 'outlook');
  try {
    const { state, challenge } = await startPkce(email);
    url.searchParams.set('sso_state', state);
    url.searchParams.set('sso_challenge', challenge);
  } catch {
    console.warn('could not start the PKCE login, continuing without it');
  }
  const response = await fetch(url.toString(), {
    method: 'POST',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    throw new Error(`session/user returned ${response.status}`);
  }
  return (await response.json()) as User;
}

export const Login = (): JSX.Element => {
  const { post: postAuthenticate } = useFetch<
    | {
        accessToken: string;
      }
    | {
        default_message: string;
      }
  >(API_BASE_URL() + '/authenticate');
  // Strips code/state/token from the address bar (already done by main.tsx
  // in the app) and hands them over once.
  const [callback] = useState(captureCallbackParams);
  const [email] = useState(
    () => new URL(window.location.href).searchParams.get('email') || '',
  );
  const isSSOCallback = !!(callback.code || callback.state);
  const [password, setPassword] = useState<string>();
  const [token, setToken] = useState<string>();
  const [loginEmail, setLoginEmail] = useState<string>(email);
  const [errorMsg, setErrorMsg] = useState<string>();
  const [ssoFailure, setSSOFailure] = useState<{
    reason: SSOFailure;
    email: string;
  }>();
  const [data, setData] = useState<User>();
  const [loading, setLoading] = useState(!isSSOCallback && !callback.token);
  const exchangeStarted = useRef(false);
  const intl = useIntl();

  function handleEmailChange(e: React.ChangeEvent<HTMLInputElement>) {
    setLoginEmail(e.target.value);
  }

  // Start of a login: look up how the user signs in.
  useEffect(() => {
    if (isSSOCallback || callback.token) return;
    let cancelled = false;
    checkSSO(email)
      .then(user => {
        if (!cancelled) setData(user);
      })
      .catch(() => {
        if (!cancelled) setData(undefined);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [email, isSSOCallback, callback.token]);

  // Back from SSO with a code: exchange it for the session token.
  useEffect(() => {
    if (!isSSOCallback || exchangeStarted.current) return;
    exchangeStarted.current = true;
    void exchangeCode(callback.code, callback.state).then(result => {
      if ('token' in result) {
        setToken(result.token);
      } else {
        setSSOFailure({ reason: result.reason, email: result.email });
      }
    });
  }, [isSSOCallback, callback.code, callback.state]);

  // Legacy: a kvasir without the code exchange sends ?token=. Remove once
  // kvasir no longer does (DEV-1032 step K2).
  useEffect(() => {
    if (!isSSOCallback && callback.token) {
      setToken(callback.token);
    }
  }, [isSSOCallback, callback.token]);

  const isProvisioned = data?.is_provisioned;
  const redirectUrl = data?.redirect_url;

  function _loginClicked(): void {
    setErrorMsg('');
    void postAuthenticate({ userName: loginEmail, password: password }).then(
      resp => {
        if ('accessToken' in resp) {
          setToken(resp.accessToken);
        } else {
          setErrorMsg(resp.default_message);
        }
      },
    );
  }

  function _redirectClicked(): void {
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  }

  function _restartClicked(): void {
    const restartEmail = ssoFailure?.email || '';
    window.location.assign(
      '/login' +
        (restartEmail ? '?email=' + encodeURIComponent(restartEmail) : ''),
    );
  }

  useEffect(() => {
    if (!token) return;
    storeValue('precisely-token', token);
    Office.context.ui.messageParent(token);
  }, [token]);

  if (isSSOCallback) {
    return (
      <>
        <h1>{intl.formatMessage(messages.requestAccess)}</h1>
        {!ssoFailure && (
          <Spinner
            data-testid="sso-exchanging"
            label={intl.formatMessage(messages.signingIn)}
            ariaLive="assertive"
            labelPosition="left"
            size={SpinnerSize.large}
          />
        )}
        {ssoFailure && (
          <Stack tokens={{ childrenGap: 20 }} verticalFill>
            <MessageBar
              messageBarType={MessageBarType.error}
              isMultiline={true}
            >
              <p data-testid="sso-error">
                {intl.formatMessage(
                  ssoFailure.reason === 'rate_limited'
                    ? messages.ssoRateLimited
                    : messages.ssoFailedLogInAgain,
                )}
              </p>
            </MessageBar>
            <PrimaryButton
              data-testid="sso-retry-btn"
              onClick={_restartClicked}
            >
              {intl.formatMessage(messages.logInAgain)}
            </PrimaryButton>
          </Stack>
        )}
      </>
    );
  }

  return (
    <>
      <div style={{ textAlign: 'center' }}>
        <img
          width={128}
          style={{ paddingRight: '1em', marginRight: '1em' }}
          src="/assets/Microsoft_Office_Outlook_(2018-present).svg"
        ></img>
        <img
          width={128}
          style={{ paddingLeft: '1em', marginLeft: '1em' }}
          src="/assets/Precisely_symbol_black.svg"
        ></img>
      </div>
      <h1>{intl.formatMessage(messages.requestAccess)}</h1>
      <p>{intl.formatMessage(messages.appAccessInfo)}</p>
      {!loading && (
        <>
          {isProvisioned && (
            <Stack tokens={{ childrenGap: 20 }} verticalFill>
              <PrimaryButton
                data-testid="redirect-btn"
                onClick={_redirectClicked}
              >
                {intl.formatMessage(messages.grantAccess)}
              </PrimaryButton>
            </Stack>
          )}
          {!isProvisioned && (
            <Stack tokens={{ childrenGap: 20 }} verticalFill>
              <Stack.Item>
                <TextField
                  label={intl.formatMessage(messages.emailLabel)}
                  value={loginEmail}
                  onChange={handleEmailChange}
                />
                <TextField
                  label={intl.formatMessage(messages.passwordLabel)}
                  type="password"
                  canRevealPassword
                  revealPasswordAriaLabel={intl.formatMessage(
                    messages.showPassword,
                  )}
                  onChange={(_, value) => {
                    setPassword(value);
                  }}
                />
              </Stack.Item>
              <PrimaryButton data-testid="login-btn" onClick={_loginClicked}>
                {intl.formatMessage(messages.loginButton)}
              </PrimaryButton>
              {errorMsg && (
                <>
                  <MessageBar
                    messageBarType={MessageBarType.error}
                    isMultiline={true}
                    //onDismiss={p.resetChoice}
                    dismissButtonAriaLabel={intl.formatMessage(messages.close)}
                  >
                    <p>{intl.formatMessage(messages.failedToLogin)}</p>
                    {errorMsg}
                  </MessageBar>
                </>
              )}
            </Stack>
          )}
        </>
      )}
    </>
  );
};

export default Login;
